'use client';

import { useEffect } from 'react';

const PLACEHOLDER_BASE_URL = 'https://placehold.co';
const DEFAULT_PLACEHOLDER_WIDTH = 600;
const DEFAULT_PLACEHOLDER_HEIGHT = 400;

const clampDimension = (value, fallback) => {
    const number = Number.parseInt(value, 10);

    if (!Number.isFinite(number) || number <= 0) {
        return fallback;
    }

    return Math.min(Math.max(number, 1), 2000);
};

const getImageDimension = (image, dimension, fallback) => {
    const rect = image.getBoundingClientRect();
    const renderedValue = dimension === 'width' ? rect.width : rect.height;
    const attributeValue = image.getAttribute(dimension);
    const propertyValue = image[dimension];

    return clampDimension(renderedValue || attributeValue || propertyValue, fallback);
};

const getPlaceholderUrl = (image) => {
    const width = getImageDimension(image, 'width', DEFAULT_PLACEHOLDER_WIDTH);
    const height = getImageDimension(image, 'height', DEFAULT_PLACEHOLDER_HEIGHT);

    return `${PLACEHOLDER_BASE_URL}/${width}x${height}`;
};

const usePlaceholderForImage = (image) => {
    if (!(image instanceof HTMLImageElement)) {
        return;
    }

    if (image.dataset.placeholderFallbackApplied === 'true') {
        return;
    }

    image.dataset.placeholderFallbackApplied = 'true';
    image.srcset = '';
    image.src = getPlaceholderUrl(image);
};

export default function FrontImageFallbackGuard() {
    useEffect(() => {
        const handleImageError = (event) => {
            usePlaceholderForImage(event.target);
        };

        const replaceMissingImages = (root = document) => {
            root.querySelectorAll('img').forEach((image) => {
                if (!image.getAttribute('src') && !image.getAttribute('srcset')) {
                    usePlaceholderForImage(image);
                }
            });
        };

        replaceMissingImages();
        window.addEventListener('error', handleImageError, true);

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof HTMLImageElement) {
                        if (!node.getAttribute('src') && !node.getAttribute('srcset')) {
                            usePlaceholderForImage(node);
                        }
                        return;
                    }

                    if (node instanceof HTMLElement) {
                        replaceMissingImages(node);
                    }
                });
            });
        });

        observer.observe(document.body, { childList: true, subtree: true });

        return () => {
            window.removeEventListener('error', handleImageError, true);
            observer.disconnect();
        };
    }, []);

    return null;
}
